/**
 * Tank Service Layer
 *
 * API 호출 및 Domain 모델 변환을 담당
 * Layered Architecture 패턴 적용
 */

import type {
  TankListApiResponse,
  TankDetailApiResponse,
  CreateTankApiRequest,
  StockFryApiRequest,
  EmptyTankApiRequest,
  GradeApiRequest,
  DailyRecordsApiResponse,
  UpsertDailyRecordsApiRequest,
  EmptyTanksApiResponse,
} from '@/types/api'

import type {
  Tank,
  TankWithSchool,
  CreateTankCommand,
  StockFryCommand,
  EmptyTankCommand,
  GradeCommand,
  DailyRecord,
  UpsertDailyRecordsCommand,
} from '@/types/domain'

import {
  toDomainTank,
  toDomainTanks,
  toDomainTankWithSchool,
  toApiCreateTank,
  toApiStockFry,
  toApiEmptyTank,
  toApiGrade,
  toApiUpsertDailyRecords,
} from '@/types/mappers'

import { getData, postData } from '@/utils'

// ============================================
// Tank Queries (조회)
// ============================================

/**
 * Tank 목록 조회
 *
 * @returns Domain Tank 배열
 */
export async function getTanks(): Promise<Tank[]> {
  try {
    // getData는 axios interceptor에서 이미 {success, data} 형식을 unwrap함
    // 따라서 직접 TankListApiResponse가 반환됨
    const apiResponse = await getData<TankListApiResponse>('/tank')

    return toDomainTanks(apiResponse)
  } catch (error) {
    console.error('Failed to fetch tanks:', error)
    throw error
  }
}

/**
 * 빈 수조 목록 조회
 *
 * @returns Domain Tank 배열 (빈 수조만)
 */
export async function getEmptyTanks(): Promise<Tank[]> {
  try {
    const apiResponse = await getData<EmptyTanksApiResponse>('/tank/empty')

    return toDomainTanks(apiResponse as TankListApiResponse)
  } catch (error) {
    console.error('Failed to fetch empty tanks:', error)
    throw error
  }
}

/**
 * Tank 상세 조회 (School 정보 포함)
 *
 * @param tankId - Tank ID
 * @returns Domain TankWithSchool
 */
export async function getTankById(tankId: string): Promise<TankWithSchool> {
  try {
    // getData는 axios interceptor에서 이미 {success, data} 형식을 unwrap함
    // 따라서 직접 TankDetailApiResponse가 반환됨
    const apiResponse = await getData<TankDetailApiResponse>(`/tank/${tankId}`)

    return toDomainTankWithSchool(apiResponse)
  } catch (error) {
    console.error('Failed to fetch tank by ID:', error)
    // toast 제거 - 개별 tank 조회 실패는 silent 처리 (bulk 호출 시 너무 많은 toast 방지)
    throw error
  }
}

/**
 * 일일 기록 조회
 *
 * @param tankIds - Tank ID 배열
 * @param date - 조회 날짜 (YYYYMMDD)
 * @returns Domain DailyRecord 배열
 */
export async function getDailyRecords(
  tankIds: string[],
  date: string
): Promise<DailyRecord[]> {
  try {
    const queryParams = new URLSearchParams()
    tankIds.forEach((id) => queryParams.append('tank_ids', id))
    queryParams.append('date', date)

    const apiResponse = await getData<DailyRecordsApiResponse>(
      `/tank/daily-records?${queryParams.toString()}`
    )

    // API 응답을 Domain 모델로 변환
    return apiResponse.map((record) => ({
      tankId: record.tank_id,
      date: new Date(), // API에서 date 반환 안 함, 파라미터 재사용
      nh3: record.nh3,
      no2: record.no2,
      sampleWeight: record.sample_weight,
      stockCount: 0, // API 응답에 없음
      mortalityCount: record.mortality_count,
      harvestCount: record.harvest_count,
      currentCount: record.current_count,
    }))
  } catch (error) {
    console.error('Failed to fetch daily records:', error)
    throw error
  }
}

// ============================================
// Tank Commands (생성/수정)
// ============================================

/**
 * Tank 생성
 *
 * @param command - CreateTankCommand
 */
export async function createTank(command: CreateTankCommand): Promise<void> {
  try {
    const apiRequest = toApiCreateTank(command)
    await postData('/tank', apiRequest)
  } catch (error) {
    console.error('Failed to create tank:', error)
    throw error
  }
}

/**
 * 치어 입식
 *
 * @param command - StockFryCommand
 */
export async function stockFry(command: StockFryCommand): Promise<{ schoolId: string }> {
  try {
    const apiRequest = toApiStockFry(command)
    return await postData<{ schoolId: string }>(`/tank/${command.tankId}/stock-fry`, apiRequest)
  } catch (error) {
    console.error('Failed to stock fry:', error)
    throw error
  }
}

/**
 * 수조 비우기
 *
 * @param command - EmptyTankCommand
 */
export async function emptyTank(command: EmptyTankCommand): Promise<void> {
  try {
    const apiRequest = toApiEmptyTank(command)
    await postData(`/tank/${command.tankId}/empty-tank`, apiRequest)
  } catch (error) {
    console.error('Failed to empty tank:', error)
    throw error
  }
}

/**
 * 분조 (Grade)
 *
 * @param command - GradeCommand
 */
export async function grade(command: GradeCommand): Promise<void> {
  try {
    const apiRequest = toApiGrade(command)
    await postData('/tank/grade', apiRequest)
  } catch (error) {
    console.error('Failed to grade tanks:', error)
    throw error
  }
}

/**
 * 일일 기록 등록/수정 (복수)
 *
 * @param command - UpsertDailyRecordsCommand
 */
export async function upsertDailyRecords(
  command: UpsertDailyRecordsCommand
): Promise<void> {
  try {
    const apiRequest = toApiUpsertDailyRecords(command)
    await postData('/tank/daily-records', { list: apiRequest })
  } catch (error) {
    console.error('Failed to upsert daily records:', error)
    throw error
  }
}

// ============================================
// Legacy Compatibility (기존 API 호환)
// ============================================

/**
 * @deprecated Use getTanks() instead
 *
 * 기존 callback 스타일 API 호환용
 */
export function getTankList(setState: (tanks: Tank[]) => void): void {
  getTanks()
    .then((tanks) => setState(tanks))
    .catch((error) => console.error('getTankList error:', error))
}
